import { BsCursor, BsDash, BsEnvelopeOpen, BsEye, BsSlashCircle } from 'react-icons/bs';

import CrmCompanyLink from '#crmFeature/components/common/CrmCompanyLink/CrmCompanyLink';
import EnumFilterControl from '#globalComponents/Table/filters/EnumFilterControl';
import HeaderFilter from '#globalComponents/Table/filters/HeaderFilter';
import YesNoFilterControl from '#globalComponents/Table/filters/YesNoFilterControl';
import { formatDateWithTime } from '#lib/dateUtils';

import { EMAIL_SEND_STATUS_OPTIONS } from '../../constants/emailSendFilters';

import StatusBadge from './StatusBadge';

const EmptyMarker = () => <BsDash className="h-5 w-5 inline-block text-bgBase-800" />;

const SENT_ONLY_HINT = '„Nein“ zeigt nur versendete E-Mails.';
const BACK_TO_STATISTICS_LABEL = 'Zurück zur Statistik';

const formatSendDate = (email) => {
	if (email.status === 'sent' && email.sendAt) return formatDateWithTime(email.sendAt);
	if (email.scheduledAt) return formatDateWithTime(email.scheduledAt);
	return '-';
};

/**
 * Column config of the campaign sends table: status filter in the status header, yes/no filters in
 * the opened, clicked and unsubscribed headers.
 *
 * @param {Object} params
 * @param {Object} params.filters - Active sends-table filters
 * @param {(partial: Object) => void} params.onFilterChange
 * @param {Object<string, number>} [params.statusCounts] - Rows per status option, shown in the filter
 * @param {Set<string>} params.unsubscribedLeadIds - CRM company ids that unsubscribed
 * @param {(email: Object) => void} params.onPreview
 * @param {boolean} params.hasCrm - Links the recipient to its CRM company page
 * @returns {Array<Object>}
 */
export const buildEmailSendColumns = ({
	filters,
	onFilterChange,
	statusCounts,
	unsubscribedLeadIds,
	onPreview,
	hasCrm,
}) => {
	const yesNoFilter = (paramKey, heading, hint) => (
		<YesNoFilterControl
			heading={heading}
			paramKey={paramKey}
			value={filters[paramKey]}
			hint={hint}
			onChange={onFilterChange}
		/>
	);
	return [
		{
			key: 'recipient',
			label: 'Empfänger E-Mail',
			minWidth: 180,
			render: (email) =>
				hasCrm && email.crmCompanyId ? (
					<CrmCompanyLink
						companyId={email.crmCompanyId}
						contactId={email.crmContactId}
						backLabel={BACK_TO_STATISTICS_LABEL}
					>
						{email.emailRecipient || '-'}
					</CrmCompanyLink>
				) : (
					<span className="text-text">{email.emailRecipient || '-'}</span>
				),
		},
		{
			key: 'company',
			label: 'Firma',
			minWidth: 160,
			render: (email) => <span className="block max-w-xs truncate">{email.companyName || '-'}</span>,
		},
		{
			key: 'subject',
			label: 'Betreff',
			minWidth: 160,
			render: (email) => <span className="block max-w-xs truncate">{email.emailSubject || '-'}</span>,
		},
		{
			key: 'status',
			label: 'Status',
			minWidth: 120,
			headerAccessory: (
				<HeaderFilter label="Status filtern" active={Boolean(filters.status)}>
					<EnumFilterControl
						heading="Status"
						paramKey="status"
						options={EMAIL_SEND_STATUS_OPTIONS}
						value={filters.status}
						counts={statusCounts}
						onChange={onFilterChange}
					/>
				</HeaderFilter>
			),
			render: (email) => <StatusBadge status={email.status} errorMessage={email.errorMessage} />,
		},
		{
			key: 'date',
			label: 'Datum',
			minWidth: 120,
			render: (email) => <span className="text-bgBase-800">{formatSendDate(email)}</span>,
		},
		{
			key: 'opened',
			label: 'Geöffnet',
			align: 'center',
			width: 110,
			headerAccessory: yesNoFilter('opened', 'Geöffnet', SENT_ONLY_HINT),
			render: (email) =>
				email.status === 'sent' && email.openAt ? (
					<BsEnvelopeOpen
						className="h-5 w-5 text-primary-700 inline-block"
						title={`Geöffnet: ${formatDateWithTime(email.openAt)}`}
					/>
				) : (
					<EmptyMarker />
				),
		},
		{
			key: 'clicked',
			label: 'Geklickt',
			align: 'center',
			width: 110,
			headerAccessory: yesNoFilter('clicked', 'Geklickt', SENT_ONLY_HINT),
			render: (email) =>
				email.status === 'sent' && email.clickAt ? (
					<BsCursor
						className="h-5 w-5 text-primary-600 inline-block"
						title={`Geklickt: ${formatDateWithTime(email.clickAt)}`}
					/>
				) : (
					<EmptyMarker />
				),
		},
		{
			key: 'unsubscribed',
			label: 'Abgemeldet',
			align: 'center',
			width: 110,
			headerAccessory: yesNoFilter('unsubscribed', 'Abgemeldet'),
			render: (email) =>
				email.crmCompanyId && unsubscribedLeadIds.has(email.crmCompanyId) ? (
					<BsSlashCircle className="h-5 w-5 text-caution-500 inline-block" title="Abgemeldet" />
				) : (
					<EmptyMarker />
				),
		},
		{
			key: 'preview',
			label: 'Vorschau',
			align: 'center',
			width: 80,
			render: (email) => (
				<button
					type="button"
					onClick={() => onPreview(email)}
					className="rounded-control p-2 text-bgBase-800 transition-colors hover:bg-bgBase-300 hover:text-primary-600"
					title="E-Mail ansehen"
				>
					<BsEye className="h-5 w-5" />
				</button>
			),
		},
	];
};
